import React, { useState, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import MapIcon from '@material-ui/icons/Map';
import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import clsx from 'clsx';
import {
  getRouteDeparture,
  getRouteDisplayDate,
  getRouteVrpIdentifier,
} from 'util/routeDisplayUtils';

const CustomTooltip = withStyles({
  tooltip: {
    backgroundColor: "#93B5F0 !important",
    color: "#121212 !important",
    padding: "8px 12px !important",
    borderRadius: "4px !important",
    fontSize: "12px !important",
    fontFamily: "Roboto, sans-serif !important",
    boxShadow: "0px 2px 6px rgba(0,0,0,0.3) !important",
  },
  arrow: {
    color: "#93B5F0 !important",
  },
})(Tooltip);

const styles = makeStyles((theme) => ({
  _box: {
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    padding: '10px 15px',
    border: '1px solid transparent',
    cursor: 'pointer',

    '&:hover': {
      borderColor: '#6F9CEB',
    },
  },
  blueBorder: {
    borderColor: '#6F9CEB',
  },
  textWhite: {
    color: '#FFF',
  },
  _6F9CEB: {
    color: '#6F9CEB'
  },
  padding: {
    padding: '0px 15px'
  },
  rowContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    flex: 1,
    minWidth: 0,
  },
  tourName: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  routeMeta: {
    color: '#ADADAD',
    fontSize: 12,
    marginLeft: 8,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  vrpBadge: {
    marginLeft: 6,
    fontSize: 11,
    color: '#6F9CEB',
    fontWeight: 600,
    flexShrink: 0,
  },
}));

const renderRouteLabel = (classes, route, t, loading = false) => {
  const departure = getRouteDeparture(route);
  const departureLabel = departure ? t(departure) : null;
  const vrpIdentifier = getRouteVrpIdentifier(route);
  const showVrp = route?.vrp_info != null;

  return (
    <Typography variant="body2" component="div" className={clsx(classes.textWhite, classes.rowContent)}>
      <Box component="span" mx={3} style={{ flexShrink: 0 }}><b>T{route.uuid}</b></Box>
      <CustomTooltip title={route.Tour?.name || ''} placement="top" arrow>
        <Box component="span" className={classes.tourName}>
          {route.Tour?.name}
        </Box>
      </CustomTooltip>
      {departureLabel && (
        <Box component="span" className={classes.routeMeta}>{departureLabel}</Box>
      )}
      <Box component="span" className={classes.routeMeta}>{getRouteDisplayDate(route)}</Box>
      {showVrp && (
        <Box component="span" className={classes.vrpBadge} title={vrpIdentifier || 'VRP'}>
          VRP
        </Box>
      )}
      {loading && (
        <Box component="span" ml={1} style={{ flexShrink: 0 }}>
          <CircularProgress size={20} color="secondary" />
        </Box>
      )}
    </Typography>
  );
};

export default ({
  routes,
  selectedRoutes,
  selectedRoutesIds,
  onSelect,
  onRemove,
  highlightRoute,
  highlightedRouteId,
}) => {
  const classes = styles();
  const { t } = useTranslation();
  const [loading, setLoading] = useState([]);

  useEffect(() => {
    setLoading((l) => l.filter((i) => !selectedRoutesIds.includes(i)));
  }, [selectedRoutesIds]);

  const handleOnSelect = (route) => {
    setLoading(loading.concat(route.id));

    return onSelect(route);
  };

  return (
    <Box>
      <Box my={4}>
        <Typography className={clsx(classes.textWhite, classes.padding)} variant="h6">
          {t('On map')}
        </Typography>
      </Box>
      {
        selectedRoutes.map((route, i) => {
          return (
            <Box
              className={clsx(classes._box, {
                [classes.blueBorder]: highlightedRouteId === route.id,
              })}
              style={{ background: i % 2 === 0 ? ' #1F1F1F ' : '#525252', }}
              key={i}
              onClick={() => onRemove(route)}
              onMouseEnter={() => highlightRoute(route.id)}
              onMouseLeave={() => highlightRoute(null)}
            >
              <MapIcon className={classes._6F9CEB} />
              {renderRouteLabel(classes, route, t)}
            </Box>
          );
        })
      }
      <Box my={4}>
        <Typography className={clsx(classes.textWhite, classes.padding)} variant="h6">
          {t('All tours')}
        </Typography>
      </Box>
      {
        routes.map((route, i) => {
          return (
            <Box
              className={classes._box}
              style={{ background: i % 2 === 0 ? ' #1F1F1F ' : '#525252', }}
              key={i}
              onClick={() => handleOnSelect(route)}
            >
              <MapIcon />
              {renderRouteLabel(classes, route, t, loading.includes(route.id))}
            </Box>
          );
        })
      }
    </Box>
  )
};
